import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { action, computed, observable } from 'mobx'
import { collection } from '@/infra/collection'
import { User } from '@/features/iam/users/User'
import type { JobStatus } from '@/features/jobs/job-status'
import {
  type JobType,
  matchJobType,
  type MatchJobTypeArms,
  MiscJobType,
  TaxJobType,
  ConsultationJobType,
  type JobTypeProps,
  BookkeepingJobType,
} from '@/features/jobs/job-type'
import { unixEpoch } from '@/utils/date-util'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { JobLegend } from '@/features/jobs/job-legend'
import type { JobIntakeResponse } from '@/features/jobs/job-intake-response'
import type { JobScopeChangeStatus } from '@/features/jobs/job-scope-changes/JobScopeChange'

export type SubmittedJobProps = ModelProps<SubmittedJob> & {
  collaborators: CollaboratorProps[]
  type: JobTypeProps
}

/**
 * Detailed information about a Submitted Job.
 */
export class SubmittedJob extends Model<SubmittedJob> {
  @observable private accessor _type: JobType = new MiscJobType()

  id = Id.none
  shortId = Id.none

  @observable accessor title = ''
  @observable accessor legendId = Id.none
  @observable accessor conversationId = Id.none
  @observable accessor legendName = ''
  @observable accessor status: JobStatus = 'UnderConstruction'
  @observable accessor submittedAt = unixEpoch()
  @observable accessor closedAt = unixEpoch()
  @observable accessor workspace = Workspace.none

  @observable accessor jobScopeItems: JobScopeItem[] = []
  @observable.struct accessor answers: JobIntakeResponse[] = []

  @observable accessor total = 0
  @observable accessor primaryAmount = 0
  @observable accessor couponAmount = 0
  @observable accessor amendedAmount = 0
  @observable accessor couponCode = ''
  @observable accessor legend = JobLegend.none
  @observable accessor jobScopeChangeStatus: JobScopeChangeStatus =
    'Unspecified'

  /**
   * The collaborators on the job.
   */
  readonly collaborators = collection({
    create: () => new Collaborator(),
    getDataId: (d) => d.user?.id,
    getModelId: (m) => m.user.id,
  })

  /**
   * The underlying job type properties.
   */
  @computed
  get type() {
    return this._type
  }

  /**
   * Gets the champion Pro on the job, if any.
   */
  @computed
  get championPro() {
    return this.collaborators.items.find(
      (c) => c.role === 'Champion' && c.type === 'Pro',
    )
  }

  /**
   * Gets the client on the job, if any.
   */
  @computed
  get championClient() {
    return this.collaborators.items.find(
      (c) => c.role === 'Champion' && c.type === 'Client',
    )!
  }

  /**
   * Whether rating a pro is enabled.
   */
  @computed
  get isProRatingEnabled() {
    return this.legend.ratingConfig.proRatingEnabled
  }

  /**
   * Whether billing is enabled for the job.
   */
  @computed
  get isBillingEnabled() {
    return this.legend.isBillingEnabled && this.workspace.config.billing.enabled
  }

  /**
   * Whether rating the platform is enabled.
   */
  @computed
  get isPlatformRatingEnabled() {
    return this.legend.ratingConfig.platformRatingEnabled
  }

  /**
   * @inheritDoc
   */
  @action.bound
  override set({
    collaborators,
    type,
    ...props
  }: Partial<SubmittedJobProps>): this {
    this.collaborators.reset(collaborators)

    // Type is a bit special because it's a nested model where the
    // concrete type is a different class based on the value of `type`,
    // so to create an instance we need to do a bit of work, and to preserve
    // instance identity we need to check the type before potentially replacing
    // the instance.
    // This isn't very commonly needed.
    if (type) {
      if (type.type === 'Misc') {
        if (this._type instanceof MiscJobType) {
          this._type.set(type)
        } else {
          this._type = new MiscJobType().set(type)
        }
      }

      if (type.type === 'Tax') {
        if (this._type instanceof TaxJobType) {
          this._type.set(type)
        } else {
          this._type = new TaxJobType(this).set(type)
        }
      }

      if (type.type === 'Consultation') {
        if (this._type instanceof ConsultationJobType) {
          this._type.set(type)
        } else {
          this._type = new ConsultationJobType(this).set(type)
        }
      }

      if (type.type === 'Bookkeeping') {
        if (this._type instanceof BookkeepingJobType) {
          this._type.set(type)
        } else {
          this._type = new BookkeepingJobType().set(type)
        }
      }
    }

    return super.set(props)
  }

  /**
   * Match on the job type.
   */
  match<T>(arms: MatchJobTypeArms<T>): T {
    return matchJobType<T>(this.type, arms)
  }
}

/**
 * The job scope item.
 */
export interface JobScopeItem {
  id: Id
  name: string
  quantity: number
  bundleId: Id | null
}

export type CollaboratorType = 'Client' | 'Pro'
export type CollaboratorRole = 'Champion' | 'Supporting'

export type CollaboratorProps = ModelProps<Collaborator>

/**
 * A job collaborator.
 */
export class Collaborator extends Model<Collaborator> {
  /**
   * The backing user.
   */
  user = User.none

  /**
   * The collaborator type.
   */
  @observable accessor type: CollaboratorType = 'Client'

  /**
   * The collaborator role.
   */
  @observable accessor role: CollaboratorRole = 'Champion'
}
